import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http'
import { AlertController } from 'ionic-angular'

import { Observable } from 'rxjs/Observable'
import 'rxjs/add/operator/map'

import { httpHost } from '../config'
import { PaginatedList } from '../libs/infinite-paginator'
import { Store } from '@ngrx/store'
import { AppState } from '../reducers'
import { Unauthorized } from '../actions/auth'
import { HttpErrorResponse } from '@angular/common/http'
import { normalizeKeys } from '../libs/object-keys-normalizer'
import { BaseModel, ModelType } from '../models/base-model'
import * as qs from 'qs'

@Injectable()
export class LectureApiBase {
  protected urlBase: string

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private alertCtrl: AlertController,
  ) {
    this.urlBase = `${httpHost()}/api/v1/`
  }

  makeUrlWithParams(url: string, params: Object): string {
    const paramsStr = qs.stringify(normalizeKeys(params, 'snake'), { arrayFormat: 'brackets' })
    const separator = url.includes('?') ? '&' : '?'  // if url includes '?', append querystring because url has params
    return url + separator + paramsStr
  }

  getRaw(url: string): Observable<string>{
    return this.http.get(this.makeUrl(url), { observe: 'body', responseType: 'text' })
  }
  getWithNoFilter(url: string): Observable<any> {
    return this.http.get<any>(this.makeUrl(url),
      { observe: 'response', headers: this.buildHeaders(false) })
      .do(null, (resp) => { if (resp.status === 401) this.unAuthorized() })
  }
  get(url: string): Observable<any> {
    return this.getWithNoFilter(url)
      .map(this.onSuccessRequest.bind(this))
  }

  getWithObject<T extends BaseModel>(modelType: ModelType<T>, url: string): Observable<T> {
    return this.get(url).map((r) => new modelType(r))
  }

  listWithObject<T extends BaseModel>(modelType: ModelType<T>, url: string): Observable<T[]> {
    return this.get(url).map((arr: any[]) => arr.map(r => new modelType(r)))
  }

  listWithPaginate<T extends BaseModel>(modelType: ModelType<T>, url: string, page = 1, size = 25, query = {}): PaginatedList<T> {
    const loadPage = (page) => {
      Object.assign(query, { page: { size: size, number: page } })
      return this.getWithNoFilter(this.makeUrlWithParams(url, normalizeKeys(query, 'snake')))
        .map((arr) => ({ items: normalizeKeys(arr.body, 'camel').map((a) => new modelType(a)), total: arr.headers.get('X-Total'), next: page + 1, prev: page - 1 }))
    }
    return new PaginatedList<T>(loadPage, page)
  }

  /**
   * POST バイナリペイロード (e.g. ArrayBuffer) を送信
   * @param url     API エンドポイント (urlBase からの相対)
   * @param body    ArrayBuffer などのバイナリデータ
   * @param params  クエリパラメータオブジェクト
   */
  postBinary(
    url: string,
    body: ArrayBuffer,
    params: Object = {}
  ): Observable<any> {
    const fullUrl = this.makeUrlWithParams(url, params)
    return this.http.post(
      `${this.urlBase}${fullUrl}`,
      body,
      {
        headers: this.buildBinaryHeaders(),
        observe: 'response',
        responseType: 'text' as 'json'
      }
    )
    .do(null, (resp: HttpResponse<any>) => { if (resp.status === 401) this.unAuthorized() })
    .map(this.onSuccessRequest.bind(this))
  }

  post(url: string, bodyObj: any): Observable<any> {
    return this.http.post(this.makeUrl(url), normalizeKeys(bodyObj, 'snake'),
      { observe: 'response', headers: this.buildHeaders(true) })
      .do(null, (resp) => { if (resp.status === 401) this.unAuthorized() })
      .map(this.onSuccessRequest.bind(this))
  }

  postWithObject<T extends BaseModel>(modelType: ModelType<T>, url: string, bodyObj: any): Observable<T> {
    return this.post(url, bodyObj).map((r) => new modelType(r))
  }

  postHaveStatus(url: string, bodyObj: any): Observable<any> {
    return this.http.post(this.makeUrl(url), bodyObj,
      { observe: 'response', headers: this.buildHeaders(true) })
      .do(null, (resp) => { if (resp.status === 401) this.unAuthorized() })
      .map((res) => { return this.onSuccessRequest(res, { withStatus: true }) })
  }

  postWithObjectHaveStatus<T extends BaseModel>(modelType: ModelType<T>, url: string, bodyObj: any): Observable<T> {
    return this.postHaveStatus(url, bodyObj).map((r) => new modelType(r))
  }

  put(url: string, bodyObj: any): Observable<any> {
    return this.http.put(this.makeUrl(url), normalizeKeys(bodyObj, 'snake'),
      { observe: 'response', headers: this.buildHeaders(true) })
      .do(null, (resp) => { if (resp.status === 401) this.unAuthorized() })
      .map(this.onSuccessRequest.bind(this))
  }

  putWithObject<T extends BaseModel>(modelType: ModelType<T>, url: string, bodyObj: any): Observable<T> {
    return this.put(url, bodyObj).map((r) => new modelType(r))
  }

  delete(url: string): Observable<any> {
    return this.http.delete(this.makeUrl(url),
      { observe: 'response', headers: this.buildHeaders(false) })
      .do(null, (resp) => { if (resp.status === 401) this.unAuthorized() })
      .map(this.onSuccessRequest.bind(this))
  }

  private makeUrl(url: string): string { return `${this.urlBase}${url}` }

  private onSuccessRequest(res: HttpResponse<any>, option: { withStatus?: boolean, withHeader?: boolean } = {}): any {
    if (res.body !== '') {
      if (option && option.withStatus) {
        res.body.status = res.status
      } else if (option && option.withHeader) {
        res.body.headers = res.headers
      }
      return normalizeKeys(res.body, 'camel')
    } else {
      return null
    }
  }

  private errorHandler(error: HttpErrorResponse) {
    if(error.status === 401) {
      this.unAuthorized()
    } else if(error.status === 0) {
      this.alertCtrl.create({
        title: 'サーバ接続エラー',
        message: 'サーバと正常に通信できません。通信状況を確認してください。',
        buttons: [{
          text: 'OK'
        }]
      }).present()
    }
  }

  private buildHeaders(withContentType: boolean): HttpHeaders {
    let headers = new HttpHeaders()
    if (withContentType) {
      headers = headers.set('Content-Type', 'application/json')
    }
    return headers
  }

  /** バイナリ送信用ヘッダー */
  private buildBinaryHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/octet-stream' })
  }

  private unAuthorized() {
    this.store.dispatch(new Unauthorized())
  }
}



// WEBPACK FOOTER //
// ./src/apis/lecture-api.ts